import { useState, useEffect, useCallback } from 'react'
import type { Message, SendOptions, ChatStatus, UseChatReturn, CharacterInfo, PersonaInfo } from '../types/index.js'
import { PUSH } from '../core/events.js'
import { useCharx } from './context.js'
import { deletePlatformMessage, mergePlatformMessage, updatePlatformMessage } from './message-state.js'

export function useChat(): UseChatReturn {
  const sdk = useCharx()
  const [messages, setMessages] = useState<Message[]>([])
  const [status, setStatus] = useState<ChatStatus>('idle')
  const [error, setError] = useState<Error | null>(null)
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null)
  const [currentAssistantMessageId, setCurrentAssistantMessageId] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)

  useEffect(() => {
    // Load initial messages
    sdk.chat.messages.getAll().then(msgs => {
      setMessages(msgs)
      setHasMore(msgs.length >= 20)
    }).catch(() => {})

    // Subscribe to host-side message mutations so embedded UIs stay in sync.
    const unsubs = [
      sdk.getRpc().push(PUSH.CHAT_MESSAGE, (data) => {
        setMessages(prev => mergePlatformMessage(prev, data as Message))
      }),
      sdk.chat.onMessageUpdated((message) => {
        setMessages(prev => updatePlatformMessage(prev, message))
      }),
      sdk.chat.onMessageEdited((message) => {
        setMessages(prev => updatePlatformMessage(prev, message))
      }),
      sdk.chat.onMessageDeleted((message) => {
        setMessages(prev => deletePlatformMessage(prev, message))
      }),
    ]
    return () => {
      unsubs.forEach(unsub => unsub())
    }
  }, [sdk])

  const sendStream = useCallback((message: string, options?: SendOptions) => {
    setStatus('sending')
    setError(null)
    const streamingId = `streaming_${Date.now()}`

    setMessages(prev => [
      ...prev,
      { id: `user_${Date.now()}`, role: 'user', content: message, createdAt: Date.now() },
      { id: streamingId, role: 'assistant', content: '', createdAt: Date.now() },
    ])

    let accumulated = ''
    setStatus('streaming')
    let taskRequestId = options?.requestId ?? null

    const task = sdk.chat.sendStream(message, {
      ...options,
      onStart: (payload) => {
        setCurrentRequestId(payload.requestId ?? taskRequestId)
        if (payload.assistantMessageId != null) {
          setCurrentAssistantMessageId(String(payload.assistantMessageId))
        }
        options?.onStart?.(payload)
      },
      onData: (chunk) => {
        accumulated += chunk
        setMessages(prev => prev.map(m =>
          m.id === streamingId ? { ...m, content: accumulated } : m,
        ))
        options?.onData?.(chunk)
      },
      onComplete: (msg) => {
        setMessages(prev => prev.map(m =>
          m.id === streamingId ? { ...msg, content: accumulated || msg.content } : m,
        ))
        setCurrentAssistantMessageId(msg.id)
        setStatus('idle')
        options?.onComplete?.(msg)
      },
      onError: (err) => {
        // Remove the empty streaming placeholder on error
        setMessages(prev => prev.filter(m => m.id !== streamingId))
        setError(err)
        setStatus('error')
        options?.onError?.(err)
      },
    })
    taskRequestId = task.requestId
    setCurrentRequestId(task.requestId)
    task.done.finally(() => {
      setCurrentRequestId(current => (current === task.requestId ? null : current))
    }).catch(() => {})
    return task
  }, [sdk])

  const send = useCallback((message: string, options?: SendOptions) => {
    void sendStream(message, options).done.catch(() => {})
  }, [sendStream])

  const loadMore = useCallback(async () => {
    const more = await sdk.chat.messages.list({ limit: 20, offset: messages.length })
    setMessages(prev => [...more, ...prev])
    setHasMore(more.length >= 20)
  }, [sdk, messages.length])

  return {
    messages, status, error, currentRequestId, currentAssistantMessageId,
    send,
    sendStream,
    stop:          () => sdk.chat.stop(),
    regenerate:    () => sdk.chat.regenerate(),
    continue:      () => sdk.chat.continue(),
    deleteMessage: (id) => sdk.chat.messages.delete(id).then(() =>
      setMessages(prev => prev.filter(m => m.id !== id)),
    ),
    editMessage: async (id, content) => {
      const updated = await sdk.chat.messages.edit(id, content)
      setMessages(prev => prev.map(m => m.id === id ? updated : m))
    },
    // Compatibility placeholder. Current platform hosts may reject this call.
    swipeMessage: async (id, direction) => {
      const updated = await sdk.chat.messages.swipe(id, direction)
      setMessages(prev => prev.map(m => m.id === id ? updated : m))
    },
    generateRaw: (chatMessages, options) => sdk.chat.generateRaw(chatMessages, options),
    injectPrompt: (content) => sdk.chat.injectPrompt(content),
    insertSystemMessage: async (content) => {
      const inserted = await sdk.chat.insertSystemMessage(content)
      setMessages(prev => [...prev, inserted])
      return inserted
    },
    getConversationId: () => sdk.chat.getConversationId(),
    loadMore,
    hasMore,
  }
}

export function useVariable<T = unknown>(key: string, defaultValue: T): [T, (v: T) => Promise<void>] {
  const sdk = useCharx()
  const [value, setValue] = useState<T>(defaultValue)

  useEffect(() => {
    let cancelled = false
    sdk.variable.get<T>(key).then(v => {
      if (!cancelled) setValue(v ?? defaultValue)
    }).catch(() => {})
    const unsubscribe = sdk.variable.watch<T>(key, nextValue => setValue(nextValue))
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [sdk, key, defaultValue])

  const set = useCallback(async (v: T) => { await sdk.variable.set(key, v); setValue(v) }, [sdk, key])
  return [value, set]
}

export function useVariables() {
  const sdk = useCharx()
  const [variables, setVariables] = useState<Record<string, unknown>>({})

  useEffect(() => {
    sdk.variable.getAll().then(setVariables).catch(() => {})
    return sdk.variable.watchAll(setVariables)
  }, [sdk])

  return variables
}

export function useCharacter(): CharacterInfo | null {
  const sdk = useCharx()
  const [info, setInfo] = useState<CharacterInfo | null>(null)
  useEffect(() => { sdk.character.getInfo().then(setInfo).catch(() => {}) }, [sdk])
  return info
}

export function usePersona(): PersonaInfo | null {
  const sdk = useCharx()
  const [persona, setPersona] = useState<PersonaInfo | null>(null)
  useEffect(() => { sdk.character.getPersona().then(setPersona).catch(() => {}) }, [sdk])
  return persona
}

export function useStorage<T = unknown>(key: string, defaultValue: T): [T, (v: T) => Promise<void>, () => Promise<void>] {
  const sdk = useCharx()
  const [value, setValue] = useState<T>(defaultValue)
  useEffect(() => {
    sdk.storage.getOrDefault<T>(key, defaultValue).then(setValue).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk, key])
  const set = useCallback(async (v: T) => { await sdk.storage.set(key, v); setValue(v) }, [sdk, key])
  const remove = useCallback(async () => { await sdk.storage.remove(key); setValue(defaultValue) }, [sdk, key, defaultValue])
  return [value, set, remove]
}
